import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from "../api/client";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, PointElement, LineElement } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import { DollarSign, Users, TrendingUp, AlertCircle, AlertTriangle, PiggyBank, FunnelX } from 'lucide-react';
import { formatearFecha } from '../components/shared/utils';
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, PointElement, LineElement);

// Orden de los segmentos en las donas; se usa para aplicar el filtro de dictamen al hacer clic
const ESTATUS_POR_SEGMENTO = ['Confirmado', 'Se cobró menos', 'Aclaración', 'Abuso', 'Sesgo'];

const Dashboard = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ fechaInicio: '', fechaFin: '', vOT: '', mat_OP: '', vCaseta: '', Estatus: '', rutaSeleccionada: '', vIdTipoRuta: '' });
  const { fechaInicio: vFechaInicio, fechaFin: vFechaFin, vOT, mat_OP: vmat_OP, vCaseta, Estatus: vEstatus, rutaSeleccionada, vIdTipoRuta } = filters;
  const [filtradoData, setFiltradoData] = useState([]);
  const [abusosFiltrados, setAbusosFiltrados] = useState([]);
  const [condonadosFiltrados, setCondonadosFiltrados] = useState([]);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [importeTotalCasetas, setImporteTotalCasetas] = useState(0);
  const [totalCasetasCruzadas, setTotalCasetasCruzadas] = useState(0);
  const [detalleAclaracionesAgrupadas, setDetalleAclaracionesAgrupadas] = useState([]);
  const [sobrecostoNoReclamado, setSobrecostoNoReclamado] = useState(0);
  const [ahorroTotal, setAhorroTotal] = useState(0);
  const [principalesRutasConDiferencia, setPrincipalesRutasConDiferencia] = useState([]);

  const formatCompactNumber = (number) => {
    if (number === undefined || number === null) return '0';
    return new Intl.NumberFormat('es-MX', {
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 2
    }).format(number);
  };

  const formatMoney = (number) => `$${(number || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    if (name === 'vIdTipoRuta') {
      // Si se selecciona una ruta, se actualiza el filtro de rutaSeleccionada con el Id_tipo_ruta correspondiente
      const rutaSeleccionada = data?.rutasActivas?.find(ruta => String(ruta.Id_tipo_ruta) === val);
      setFilters((prev) => ({
        ...prev,
        rutaSeleccionada: rutaSeleccionada ? String(rutaSeleccionada.Id_tipo_ruta) : '',
        [name]: val
      }));
    }
    else if (name === 'rutaSeleccionada') {
      // Si se selecciona una ruta, se actualiza el filtro de vIdTipoRuta con el Id_tipo_ruta correspondiente
      const rutaSeleccionada = data?.rutasActivas?.find(ruta => String(ruta.Id_tipo_ruta) === val);
      setFilters((prev) => ({
        ...prev,
        vIdTipoRuta: rutaSeleccionada ? String(rutaSeleccionada.Id_tipo_ruta) : '',
        [name]: val
      }));

    }
    else {
      setFilters((prev) => ({ ...prev, [name]: val }));
    }

  };
  const resetFiltros = () => {
    setFilters({ fechaInicio: '', fechaFin: '', vOT: '', mat_OP: '', vCaseta: '', Estatus: '', rutaSeleccionada: '', vIdTipoRuta: '' });
  };

  // Rangos rápidos de fecha para no capturar manualmente
  const aplicarRangoRapido = (rango) => {
    const hoy = new Date();
    const aISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    let inicio = new Date(hoy);
    if (rango === '7d') inicio.setDate(hoy.getDate() - 6);
    if (rango === 'mes') inicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    if (rango === 'anio') inicio = new Date(hoy.getFullYear(), 0, 1);
    setFilters((prev) => ({ ...prev, fechaInicio: aISO(inicio), fechaFin: aISO(hoy) }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const p1 = apiClient.post(`/api/dashboard/getDashboardData`, {
          idOrden: vOT,
          Matricula: vmat_OP,
          FechaInicio: vFechaInicio,
          FechaFin: vFechaFin,
          id_Tipo_ruta: rutaSeleccionada
        })
          .then(res => {
            setData(res.data);
          })
          .catch(err => console.error('Error al cargar cruces:', err));

        const p2 = apiClient.get(`/api/dashboard/getPrincipalesRutasConDiferencia`).then(res => {
          setPrincipalesRutasConDiferencia(res.data?.data || []);
        }).catch(err => console.error('Error al cargar las rutas con diferencia:', err));

        await Promise.all([p1, p2]);

      } catch (error) {
      }
      finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const ExportarDatosaExcel = (nombreArchivo) => {
    import('xlsx').then(XLSX => {
      const worksheet = XLSX.utils.json_to_sheet(filtradoData);
      const worksheetAclaraciones = XLSX.utils.json_to_sheet(detalleAclaracionesAgrupadas);
      const workbook = XLSX.utils.book_new();
      //Una hoja para los datos generales
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');
      //Otra hoja para las aclaraciones
      XLSX.utils.book_append_sheet(workbook, worksheetAclaraciones, 'Aclaraciones');

      //Una hoja adicional para la descripción de los filtros aplicados
      const rutaActiva = data?.rutasActivas?.find(ruta => String(ruta.Id_tipo_ruta) === String(rutaSeleccionada));
      const filtrosAplicados = {
        'Fecha Inicio': vFechaInicio ? formatearFecha(vFechaInicio) : 'No aplicado',
        'Fecha Fin': vFechaFin ? formatearFecha(vFechaFin) : 'No aplicado',
        'OT': vOT || 'No aplicado',
        'Matricula / Operador': vmat_OP || 'No aplicado',
        'Caseta': vCaseta || 'No aplicado',
        'Dictamen': vEstatus || 'No aplicado',
        'Ruta Seleccionada': rutaActiva ? `${rutaActiva.Origen} - ${rutaActiva.Destino}` : 'No aplicado',
        'Id Tipo Ruta': vIdTipoRuta || 'No aplicado',
      };
      const filtrosSheet = XLSX.utils.json_to_sheet(Object.entries(filtrosAplicados).map(([key, value]) => ({ Filtro: key, Valor: value })));
      XLSX.utils.book_append_sheet(workbook, filtrosSheet, 'Filtros Aplicados');
      XLSX.writeFile(workbook, `${nombreArchivo}.xlsx`);
    });

  };
  useEffect(() => {

    const inicio = vFechaInicio ? new Date(vFechaInicio + 'T00:00:00') : null;
    const fin = vFechaFin ? new Date(vFechaFin + 'T23:59:59') : null;
    const OT = vOT ? vOT.toLowerCase() : '';

    const casetaLower = vCaseta ? vCaseta?.toLowerCase() : '';
    const matOpLower = vmat_OP ? vmat_OP?.toLowerCase() : '';
    const shouldFilterRuta = rutaSeleccionada && rutaSeleccionada !== '';
    /*
      Estatus secundarios agrupados en:
      Culminados
      confirmado
      dictaminado
      cobro_menor
      descuento_aplicado_pendiente_acta
      acta_aplicada_pendiente_descuento
      NULL
      Condonado


      En proceso
      aclaracion_levantada
      pendiente_reporte
      pendiente_aclaracion
    */

    const gastosFiltrados = data?.gastosCrucesAgrupados?.filter(item => {
      // Comparaciones básicas primero para evitar instanciar Date si no es necesario
      // En caso de que sea "Pendiente" el valor del Estatus se iguala a  Error, ya que en la base de datos los pendientes están marcados como Error
      if (vEstatus) {
        if (vEstatus === 'Error') {
          if (item.Estatus !== 'Error' && item.Estatus !== 'Pendiente') return false;
        } else if (item.Estatus !== vEstatus) {
          return false;
        }
      }
      if (shouldFilterRuta && String(item.Id_tipo_ruta) !== String(rutaSeleccionada)) return false;
      if (casetaLower && !item.Nombre?.toLowerCase().includes(casetaLower)) return false;
      if (matOpLower && !item.No_Economico?.toLowerCase().includes(matOpLower)) return false;

      if (OT && !item.ID_orden?.toLowerCase().includes(OT)) return false;

      // Filtro por rango de fechas


      if (inicio || fin) {
        const fechaCruce = new Date(item.Fecha);
        if (inicio && fechaCruce < inicio) return false;
        if (fin && fechaCruce > fin) return false;
      }
      return true;
    });

    const total = gastosFiltrados?.reduce((sum, item) => sum + item.Importe, 0) || 0;

    const abusosFiltrados = gastosFiltrados?.filter(item => item.Estatus === 'Abuso') || [];
    //ordenamos los abusos de mayor a menor importe
    abusosFiltrados.sort((a, b) => b.Importe - a.Importe);
    setAbusosFiltrados(abusosFiltrados || []);

    const condonadosFiltrados = gastosFiltrados?.filter(item => item.Estatus_Secundario === 'Condonado') || [];
    //ordenamos los condonados de mayor a menor importe
    condonadosFiltrados.sort((a, b) => b.Importe - a.Importe);
    setCondonadosFiltrados(condonadosFiltrados || []);

    //Filtramos únicamente las aclaraciones:
    const aclaracionesFiltradas = gastosFiltrados?.filter(item => (item.Estatus === 'Aclaración' && (item.Estatus_Secundario === 'aclaracion_levantada'))) || [];

    // KPIs monetarios: sobrecosto sin reclamar (nos cobraron de más y no hay aclaración)
    // y ahorro (se cobró menos que la tarifa oficial). Respetan los filtros aplicados.
    let sobrecosto = 0;
    let ahorro = 0;
    gastosFiltrados?.forEach(item => {
      if (item.ImporteOficial === null || item.ImporteOficial === undefined) return;
      const diferencia = (item.Importe || 0) - (item.ImporteOficial || 0);
      if (diferencia > 0 && item.Estatus !== 'Aclaración') sobrecosto += diferencia;
      if (diferencia < 0) ahorro += Math.abs(diferencia);
    });
    setSobrecostoNoReclamado(sobrecosto);
    setAhorroTotal(ahorro);

    // Datos filtrados
    setFiltradoData(gastosFiltrados || []);
    setImporteTotalCasetas(total);
    setTotalCasetasCruzadas(gastosFiltrados?.length || 0);
    setDetalleAclaracionesAgrupadas(aclaracionesFiltradas || []);

  }, [data, vFechaInicio, vFechaFin, vCaseta, vEstatus, vOT, vmat_OP, rutaSeleccionada]);

  // Configuración para Resúmenes Mensual
  const resumenMensualData = {
    labels: ['Confirmados', 'Cobro Menor', 'Aclaraciones', `Abusos`, 'Sesgos'],
    datasets: [{
      data: [filtradoData.reduce((sum, item) => sum + (item.Estatus === 'Confirmado' ? 1 : 0), 0), filtradoData.reduce((sum, item) => sum + (item.Estatus === 'Se cobró menos' ? 1 : 0), 0), filtradoData.reduce((sum, item) => sum + (item.Estatus === 'Aclaración' ? 1 : 0), 0), filtradoData.reduce((sum, item) => sum + (item.Estatus === 'Abuso' ? 1 : 0), 0), filtradoData.reduce((sum, item) => sum + (item.Estatus === 'Sesgo' ? 1 : 0), 0)],
      backgroundColor: ['#1cc88a', '#36b9cc', '#f6c23e', '#df1313ff', '#858796'],
      borderWidth: 0,
    }]
  };

  // Configuración para Presupuesto (es lo mismo que Resúmenes Mensual pero monetizado)
  const presupuestoData = {
    labels: ['Confirmados', 'Cobro Menor', 'Aclaraciones', 'Abusos', 'Sesgos'],
    datasets: [{
      data: [filtradoData.reduce((sum, item) => sum + (item.Estatus === 'Confirmado' ? item.Importe : 0), 0), filtradoData.reduce((sum, item) => sum + (item.Estatus === 'Se cobró menos' ? item.Importe : 0), 0), filtradoData.reduce((sum, item) => sum + (item.Estatus === 'Aclaración' ? item.Importe : 0), 0), filtradoData.reduce((sum, item) => sum + (item.Estatus === 'Abuso' ? item.Importe : 0), 0), filtradoData.reduce((sum, item) => sum + (item.Estatus === 'Sesgo' ? item.Importe : 0), 0)],
      backgroundColor: ['#1cc88a', '#36b9cc', '#f6c23e', '#df1313ff', '#858796'],
      borderWidth: 0,
    }]
  };

  // Histórico mensual real: agrupa los cruces filtrados por mes (antes eran datos de ejemplo)
  const historicoMensual = useMemo(() => {
    const meses = {};
    filtradoData.forEach(item => {
      if (!item.Fecha) return;
      const fecha = new Date(item.Fecha);
      if (isNaN(fecha)) return;
      const clave = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
      if (!meses[clave]) meses[clave] = { total: 0, aclaraciones: 0, abusos: 0 };
      meses[clave].total += item.Importe || 0;
      if (item.Estatus === 'Aclaración') meses[clave].aclaraciones += item.Importe || 0;
      if (item.Estatus === 'Abuso') meses[clave].abusos += item.Importe || 0;
    });
    const claves = Object.keys(meses).sort();
    const formatoMes = new Intl.DateTimeFormat('es-MX', { month: 'short', year: '2-digit' });
    return {
      labels: claves.map(clave => formatoMes.format(new Date(clave + '-01T00:00:00'))),
      datasets: [
        { label: 'Total ($)', data: claves.map(clave => meses[clave].total), backgroundColor: '#4e73df' },
        { label: 'Aclaraciones ($)', data: claves.map(clave => meses[clave].aclaraciones), backgroundColor: '#f6c23e' },
        { label: 'Abusos ($)', data: claves.map(clave => meses[clave].abusos), backgroundColor: '#e74a3b' },
      ]
    };
  }, [filtradoData]);

  // Importe por Ruta real: Top 5 rutas por importe cobrado vs tarifa oficial,
  // desde getPrincipalesRutasConDiferencia y respetando los filtros de OT, matrícula y ruta
  const importePorRutaData = useMemo(() => {
    const otLower = vOT ? vOT.toLowerCase() : '';
    const matLower = vmat_OP ? vmat_OP.toLowerCase() : '';
    const rutasAgrupadas = {};
    principalesRutasConDiferencia.forEach(item => {
      if (rutaSeleccionada && String(item.id_Tipo_ruta) !== String(rutaSeleccionada)) return;
      if (otLower && !item.ID_orden?.toLowerCase().includes(otLower)) return;
      if (matLower && !String(item.ID_matricula ?? '').toLowerCase().includes(matLower)) return;
      const clave = item.id_Tipo_ruta;
      if (!rutasAgrupadas[clave]) rutasAgrupadas[clave] = { etiqueta: `${item.Origen} - ${item.Destino}`, cobrado: 0, oficial: 0 };
      rutasAgrupadas[clave].cobrado += item.ImporteCobrado || 0;
      rutasAgrupadas[clave].oficial += item.ImporteOficial || 0;
    });
    const top5 = Object.values(rutasAgrupadas).sort((a, b) => b.cobrado - a.cobrado).slice(0, 5);
    return {
      labels: top5.map(ruta => ruta.etiqueta),
      datasets: [
        { label: 'Cobrado ($)', data: top5.map(ruta => ruta.cobrado), backgroundColor: '#4e73df' },
        { label: 'Tarifa Oficial ($)', data: top5.map(ruta => ruta.oficial), backgroundColor: '#36b9cc' },
      ]
    };
  }, [principalesRutasConDiferencia, vOT, vmat_OP, rutaSeleccionada]);

  // Al hacer clic en un segmento de la dona se aplica (o quita) el filtro de dictamen correspondiente
  const crearOpcionesDona = (prefijoDeUnidad = '') => ({
    responsive: true,
    maintainAspectRatio: false,
    onClick: (evt, elements) => {
      if (!elements?.length) return;
      const estatus = ESTATUS_POR_SEGMENTO[elements[0].index];
      setFilters(prev => ({ ...prev, Estatus: prev.Estatus === estatus ? '' : estatus }));
    },
    onHover: (evt, elements) => {
      if (evt?.native?.target) evt.native.target.style.cursor = elements?.length ? 'pointer' : 'default';
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgb(255,255,255)',
        titleColor: '#6e707e',
        bodyColor: '#858796',
        borderColor: '#dddfeb',
        borderWidth: 1,
        padding: 15,
        displayColors: false,
        caretPadding: 10,
        callbacks: {
          label: function (context) {
            const totalDataset = context.dataset.data.reduce((acc, val) => acc + (val || 0), 0);
            const porcentaje = totalDataset ? (context.parsed / totalDataset * 100).toFixed(2) : '0.00';
            return context.label + ': ' + prefijoDeUnidad + (context.parsed)?.toLocaleString() + ' (' + porcentaje + '%)';
          },
          footer: function () {
            return 'Clic para filtrar por este dictamen';
          }
        }
      }
    },
    cutout: '70%',
  });

  // Opciones para gráfico de barras vertical
  const barOptionsVertical = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#858796',
          font: {
            family: "'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto'",
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgb(255,255,255)',
        titleColor: '#6e707e',
        bodyColor: '#858796',
        borderColor: '#dddfeb',
        borderWidth: 1,
        padding: 15,
        displayColors: true,
        caretPadding: 10,
        callbacks: {
          label: function (context) {
            return context.dataset.label + ': $' + (context.parsed.y)?.toLocaleString();
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#858796',
          font: {
            family: "'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto'",
            size: 10
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: '#e3e6f0',
          borderDash: [2],
          drawBorder: false,
        },
        ticks: {
          color: '#858796',
          font: {
            family: "'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto'",
            size: 10
          }
        }
      }
    }
  };

  // Opciones para gráfico de barras horizontal
  const barOptionsHorizontal = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#858796',
          font: {
            family: "'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto'",
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgb(255,255,255)',
        titleColor: '#6e707e',
        bodyColor: '#858796',
        borderColor: '#dddfeb',
        borderWidth: 1,
        padding: 15,
        displayColors: true,
        caretPadding: 10,
        callbacks: {
          label: function (context) {
            return context.dataset.label + ': $' + (context.parsed.x)?.toLocaleString();
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          color: '#e3e6f0',
          borderDash: [2],
          drawBorder: false,
        },
        ticks: {
          color: '#858796',
          font: {
            family: "'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto'",
            size: 10
          }
        }
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#858796',
          font: {
            family: "'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto'",
            size: 10
          }
        }
      }
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, borderColor, onClick }) => (
    <div
      className="card shadow h-20 py-2"
      style={{ borderLeft: `0.25rem solid ${borderColor}`, cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick}
      title={onClick ? 'Clic para filtrar' : undefined}
    >
      <div className="card-body">
        <div className="row no-gutters align-items-center">
          <div className="col mr-2">
            <div className="text-xs font-weight-bold text-uppercase mb-1" style={{ color: borderColor }}>
              {title}
            </div>
            <div className="h5 mb-0 font-weight-bold text-gray-800">{value}</div>
            {subtitle && <div className="text-xs text-gray-600 mt-1">{subtitle}</div>}
          </div>
          <div className="col-auto">
            <Icon className="fa-2x" style={{ color: borderColor, width: '4rem', height: '4rem', opacity: 0.3 }} />
          </div>
        </div>
      </div>
    </div>
  );

  const ChartLegend = ({ data, prefijoDeUnidad = '' }) => {
    const totalLeyenda = data.datasets[0].data.reduce((acc, val) => acc + (val || 0), 0);
    return (
      <div className="row mt-5 text-center">
        {data.labels.map((label, idx) => (
          <div key={idx} className="col-6 mb-2">
            <div className="d-flex align-items-center">
              <div
                className="mr-2"
                style={{
                  width: '0.75rem',
                  height: '0.75rem',
                  borderRadius: '50%',
                  backgroundColor: data.datasets[0].backgroundColor[idx]
                }}
              ></div>
              <span className="small text-gray-600">
                {label}: {(data.datasets[0].data[idx]) !== 0 ? prefijoDeUnidad : ''} {(data.datasets[0].data[idx]?.toLocaleString()) ? data.datasets[0].data[idx].toLocaleString() : '-'} ({totalLeyenda ? ((data.datasets[0].data[idx] / totalLeyenda) * 100).toFixed(2) : '0.00'}%)
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const rutaActiva = data?.rutasActivas?.find(ruta => String(ruta.Id_tipo_ruta) === String(rutaSeleccionada));

  return (
    <div id="content-wrapper" className="d-flex flex-column" style={{ backgroundColor: '#f8f9fc' }}>
      {(loading) && <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="sr-only">Cargando...</span>
        </div>
      </div>}

      {(!loading) && <div id="content">

        {/* Contenido principal */}
        <div className="container-fluid">
          {/* Encabezado de la página */}
          <div className="d-sm-flex align-items-center justify-content-between mb-4">

            <div className="container-fluid py-4 pb-0">
              {/* Botón para exportar datos a excel */}
              <div className="float-right p-0 m-0">
                <button className="btn btn-sm btn-outline-success rounded-3" onClick={() => ExportarDatosaExcel('Reporte_Dashboard_Principal')}>
                  <i className="fas fa-file-excel fa-sm text-success-50 mr-1"></i> Exportar Datos
                </button>
              </div>
              <h1 className="h3 mb-0 text-gray-800">Dashboard Principal {rutaActiva ? `· ${rutaActiva.Origen} - ${rutaActiva.Destino}` : ''}</h1>
              <p className="mb-0 text-gray-600">Resumen general de operaciones y estadísticas</p>

            </div>
          </div>
          {/* DIV para los filtros */}
          <div className="d-flex flex-wrap gap-2 mb-3">

            {/* DIV para el filtro de fecha */}
            <div className="form-floating pr-2">
              <input type="date" name="fechaInicio" placeholder="Fecha Inicio" className="form-control form-control-sm"
                onChange={handleChange} value={vFechaInicio} id='in_fechaInicio' />
              <label className="form-label" htmlFor='in_fechaInicio'>Inicio Rango</label>
            </div>

            <div className="form-floating pr-2">
              <input type="date" name="fechaFin" placeholder="Fecha Fin" className="form-control form-control-sm"
                onChange={handleChange} value={vFechaFin} id='in_fechaFin' />
              <label className="form-label" htmlFor='in_fechaFin'>Fin Rango</label>

            </div>

            {/* Rangos rápidos de fecha */}
            <div className="btn-group align-self-center pr-2" role="group" aria-label="Rangos rápidos">
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => aplicarRangoRapido('hoy')}>Hoy</button>
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => aplicarRangoRapido('7d')}>7 días</button>
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => aplicarRangoRapido('mes')}>Mes</button>
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => aplicarRangoRapido('anio')}>Año</button>
            </div>

            {/* DIV para el filtro de OT */}
            <div className="form-floating pr-2" style={{ maxWidth: '9rem', }}>
              <input type="text" name="vOT" className="form-control form-control-sm" placeholder='OT' onChange={handleChange} value={vOT} id='in_OT' />
              <label className="form-label" htmlFor='in_OT'>OT</label>

            </div>

            {/* DIV para el filtro de Matricula / OP (No_Economico) */}
            <div className="form-floating pr-2" style={{ maxWidth: '11.5rem', }}>
              <input type="text" name="mat_OP" className="form-control form-control-sm" placeholder='Matricula / Operador' onChange={handleChange} value={vmat_OP} id='in_Matricula_Op' />
              <label className="form-label" htmlFor='in_Matricula_Op'>Matricula / Operador</label>

            </div>


            {/* DIV para el filtro de Caseta */}
            <div className="form-floating pr-2" style={{ maxWidth: '9rem', }}>
              <input type="text" name="vCaseta" placeholder="Caseta" className="form-control form-control-sm" onChange={handleChange} value={vCaseta} />
              <label className="form-label">Caseta</label>
            </div>



            <div className='col-md-2  pr-2 input-group-text mb-0 ' style={{ maxWidth: 'min-content', }}>
              <label htmlFor='selectEstatus' className='d-flex align-items-center'>
                <span className='text-muted'>Dictamen:</span>
                <select className='mx-3 p-2' name="Estatus" onChange={handleChange} value={vEstatus} id='selectEstatus' style={{ width: 'auto', height: '2.5rem' }}>
                  <option value="">Todos</option>
                  <option value="Abuso">🚫 Abusos</option>
                  <option value="Aclaración">⚠️ Aclaraciones</option>
                  <option value="Confirmado">✔️ Confirmados</option>
                  <option value="Error">ℹ️ Error</option>
                  <option value="CasetaNoEncontradaEnRuta">🚧 Cruce sin relación</option>
                  <option value="Se cobró menos">⭐ Cobro menor</option>
                  <option value="Sesgo">🌀 Sesgos</option>
                </select>
              </label>
            </div>

            <div className='col-md-5 ml-2  p-2 input-group-text mb-0 ' style={{ maxWidth: 'min-content', }}>
              <label htmlFor='rutaSeleccionada' className='d-flex align-items-center'>
                <span className='text-muted'>Ruta:</span>
                <select className='mx-3 p-2' name="rutaSeleccionada" onChange={handleChange} value={rutaSeleccionada} id='rutaSeleccionada' style={{ width: 'auto', height: '2.5rem' }}>
                  <option value="">Todos</option>
                  {data?.rutasActivas && [...new Map(data.rutasActivas.map(item => [item.Id_tipo_ruta, item])).values()].map((ruta) => (
                    <option key={ruta.Id_tipo_ruta || ruta.ID_orden} value={ruta.Id_tipo_ruta}>  {ruta.Origen} - {ruta.Destino} ({ruta.Recuento} OTs)  </option>
                  ))}
                </select>
              </label>
            </div>


            <div className="form-floating pr-2" style={{ maxWidth: '9rem', }}>
              <input type="number" step={1} min={0} name="vIdTipoRuta" placeholder="Id Tipo Ruta" className="form-control form-control-sm" onChange={handleChange} value={vIdTipoRuta} />
              <label className="form-label">Id Tipo Ruta</label>
            </div>

            <div className="ml-3 pr-1 pt-1 d-flex">
              <button className="btn btn-sm btn btn-outline-dark rounded-3" onClick={resetFiltros} title="Limpiar filtros">
                <FunnelX size={25} className="me-1" />
              </button>
            </div>

          </div>

          {/* Content Row - Stats Cards */}
          <div className="row">
            <div className="col-xl-4 col-md-6 mb-4">
              <StatCard
                title="Importe de Casetas"
                value={formatMoney(importeTotalCasetas)}
                subtitle="Período seleccionado"
                icon={DollarSign}
                borderColor="#1cc88a"
              />
            </div>

            <div className="col-xl-4 col-md-6 mb-4">
              <StatCard
                title="Cruces realizados"
                value={totalCasetasCruzadas?.toLocaleString()}
                subtitle="Período seleccionado"
                icon={TrendingUp}
                borderColor="#4e73df"
              />
            </div>

            <div className="col-xl-4 col-md-6 mb-4">
              <StatCard
                title="Aclaraciones procedentes"
                value={detalleAclaracionesAgrupadas?.length?.toLocaleString()}
                subtitle={`${formatMoney(detalleAclaracionesAgrupadas?.reduce((acc, item) => acc + item.Importe, 0))} con el filtro aplicado`}
                icon={AlertCircle}
                borderColor="#f6c23e"
                onClick={() => setFilters(prev => ({ ...prev, Estatus: prev.Estatus === 'Aclaración' ? '' : 'Aclaración' }))}
              />
            </div>

            <div className="col-xl-4 col-md-6 mb-4">
              <StatCard
                title="Sobrecosto no reclamado"
                value={formatMoney(sobrecostoNoReclamado)}
                subtitle="Cobrado de más sin aclaración levantada"
                icon={AlertTriangle}
                borderColor="#e74a3b"
              />
            </div>

            <div className="col-xl-4 col-md-6 mb-4">
              <StatCard
                title="Ahorro vs tarifa oficial"
                value={formatMoney(ahorroTotal)}
                subtitle="Cruces cobrados por debajo de la tarifa"
                icon={PiggyBank}
                borderColor="#1cc88a"
                onClick={() => setFilters(prev => ({ ...prev, Estatus: prev.Estatus === 'Se cobró menos' ? '' : 'Se cobró menos' }))}
              />
            </div>

            <div className="col-xl-4 col-md-6 mb-4">
              <StatCard
                title="Rutas Activas"
                value={(data?.rutasActivas?.length) ? data?.rutasActivas?.length?.toLocaleString() : "0"}
                subtitle="De acuerdo al filtro aplicado"
                icon={Users}
                borderColor="#6f42c1"
              />
            </div>
          </div>

          {/* Content Row - Charts */}
          <div className="row">
            {/* Resúmenes Mensual */}
            <div className="col-xl-6 col-lg-6">
              <div className="card shadow mb-4">
                <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between">
                  <h6 className="m-0 font-weight-bold text-primary">Resumen por Dictamen</h6> <h5 className='float-right'>({(totalCasetasCruzadas)?.toLocaleString()} Cruces)</h5>
                </div>
                <div className="card-body">
                  <div className="chart-area" style={{ height: '300px' }}>
                    <Doughnut data={resumenMensualData} options={crearOpcionesDona()} />
                  </div>
                  <ChartLegend data={resumenMensualData} className="pt-3 mt-3" />
                  <p className="small text-center text-gray-500 mb-0 mt-2">💡 Haz clic en un segmento para filtrar por ese dictamen</p>
                </div>
              </div>
            </div>

            {/* Presupuesto */}
            <div className="col-xl-6 col-lg-6">
              <div className="card shadow mb-4">
                <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between">
                  <h6 className="m-0 font-weight-bold text-primary">Presupuesto</h6>
                  {/* Importe Total de Casetas dinamico (billones,millones,miles) */}
                  <span className="h4 mb-0 font-weight-bold text-gray-800">${formatCompactNumber(importeTotalCasetas)}</span>
                </div>
                <div className="card-body">
                  <div className="chart-area" style={{ height: '300px' }}>
                    <Doughnut data={presupuestoData} options={crearOpcionesDona('$')} />
                  </div>
                  <ChartLegend data={presupuestoData} prefijoDeUnidad='$' />
                  <p className="small text-center text-gray-500 mb-0 mt-2">💡 Haz clic en un segmento para filtrar por ese dictamen</p>
                </div>
              </div>
            </div>
          </div>

          {/* Content Row - More Charts */}
          <div className="row">
            {/* Histórico Mensual */}
            <div className="col-xl-6 col-lg-6">
              <div className="card shadow mb-4">
                <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between">
                  <h6 className="m-0 font-weight-bold text-primary">Histórico Mensual ($)</h6>
                  <h6 className='float-right m-0 font-weight-bold text-primary'>{formatMoney(importeTotalCasetas)}</h6>
                </div>
                <div className="card-body">
                  <div className="chart-bar" style={{ height: '300px' }}>
                    <Bar data={historicoMensual} options={barOptionsVertical} />
                  </div>
                </div>
              </div>
            </div>

            {/* Importe por Ruta */}
            <div className="col-xl-6 col-lg-6">
              <div className="card shadow mb-4">
                <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between">
                  <h6 className="m-0 font-weight-bold text-primary">Importe por Ruta (Top 5) · Cobrado vs Oficial</h6>
                </div>
                <div className="card-body">
                  <div className="chart-bar" style={{ height: '300px' }}>
                    <Bar data={importePorRutaData} options={barOptionsHorizontal} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* DataTable - Cruces sin Justificación (Abusos) */}
          <div className="card shadow mb-4">
            <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between">
              <h6 className="m-0 font-weight-bold text-danger">Cruces sin Justificación (Abusos) - Top 5</h6>
              <h6 className="float-right m-0 font-weight-bold text-warning">Cruces sin Justificación (Condonados) - Top 5</h6>
              <a href="/abusos" className="d-none d-sm-inline-block btn btn-sm btn-primary shadow-sm">
                <i className="fas fa-list fa-sm text-white-50 mr-1"></i> Ver todos
              </a>
            </div>
            <div className="card-body">
              <div className="row justify-content-around">
                <div className="table-responsive col-lg-5 col-md-12">
                  <table className="table table-bordered table-hover" width="100%" cellSpacing="0">
                    <thead>
                      <tr>
                        <th>Conductor</th>
                        <th>Importe</th>
                        <th>Última Fecha</th>
                        <th>Estatus</th>
                      </tr>
                    </thead>
                    <tbody>
                      {abusosFiltrados && abusosFiltrados.slice(0, 5).map((row, idx) => (
                        <tr key={idx} style={{ cursor: 'pointer' }} title="Ver detalle en Abusos" onClick={() => navigate('/abusos')}>
                          <td>{row.No_Economico}</td>
                          <td>{formatMoney(row.Importe)}</td>
                          <td>{formatearFecha(row?.Fecha)}</td>
                          <td className="font-weight-bold">{row?.Estatus_Secundario?.replaceAll('_', ' ')?.toUpperCase()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="table-responsive col-lg-5 col-md-12">
                  <table className="table table-bordered table-hover" width="100%" cellSpacing="0">
                    <thead>
                      <tr>
                        <th>Conductor</th>
                        <th>Importe</th>
                        <th>Última Fecha</th>
                        <th>Estatus</th>
                      </tr>
                    </thead>
                    <tbody>
                      {condonadosFiltrados && condonadosFiltrados.slice(0, 5).map((row, idx) => (
                        <tr key={idx} style={{ cursor: 'pointer' }} title="Ver detalle en Abusos" onClick={() => navigate('/abusos')}>
                          <td>{row.No_Economico}</td>
                          <td>{formatMoney(row.Importe)}</td>
                          <td>{formatearFecha(row?.Fecha)}</td>
                          <td className="font-weight-bold">{row?.Estatus_Secundario?.replaceAll('_', ' ')?.toUpperCase()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>}

    </div>

  );
};

export default Dashboard;
